import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  MemberType,
  TeamType,
} from "components/Dashboard/DoctorDashboard/group-practice/types/types";
import { accessControl } from "./roleAccessControl";
import { useUser } from "contexts/UserContext";
import getTeamDetailsById from "services/team/getTeamDetailsById";

type TeamContextType = {
  currentMember: MemberType | null;
  setCurrentMember: (member: MemberType | null) => void;
  currentTeam: TeamType | null;
  setCurrentTeam: (team: TeamType | null) => void;
  teamMembers: MemberType[];
  setTeamMembers: (members: MemberType[]) => void;
  canAccessFeatureByRole: (feature: string) => boolean;
  error: any;
  loading: boolean;
  setError: (error: any) => void;
  setLoading: (loading: boolean) => void;
  setReload: (val: any) => void;
  practiceOwnerOrchidUserId: string | undefined;
};

const TeamContext = createContext<TeamContextType | undefined>(undefined);

export const TeamProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentMember, setCurrentMember] = useState<MemberType | null>(null);
  const [currentTeam, setCurrentTeam] = useState<TeamType | null>(null);
  const [teamMembers, setTeamMembers] = useState<MemberType[]>([]);
  const [reload, setReload] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>();
  const { user } = useUser();

  useEffect(() => {
    const fetchTeamForUsers = async () => {
      setLoading(true);
      try {
        if (user?.team_id) {
          const team = await getTeamDetailsById(user?.team_id);
          const currentTeam = { id: team?.id, name: team?.name };
          setCurrentTeam(currentTeam);
          setTeamMembers(team.members);
        }
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchTeamForUsers();
  }, [user?.team_id, reload]);

  const canAccessFeatureByRole = useCallback(
    (feature: string): boolean => {
      const PracticeRole = user?.role || "OWNER";

      if (!accessControl[feature]) {
        return false;
      }

      return accessControl[feature][PracticeRole] || false;
    },
    [user],
  );
  const practiceOwnerOrchidUserId = useMemo(() => {
    if (user?.role !== "PRACTICE_SCHEDULER") {
      return undefined;
    }
    return teamMembers.find((member) => member.role === "OWNER")
      ?.orchid_user_id;
  }, [teamMembers, user?.role]);

  return (
    <TeamContext.Provider
      value={{
        currentMember,
        setCurrentMember,
        currentTeam,
        setCurrentTeam,
        teamMembers,
        setTeamMembers,
        canAccessFeatureByRole,
        error,
        loading,
        setError,
        setLoading,
        setReload,
        practiceOwnerOrchidUserId,
      }}
    >
      {children}
    </TeamContext.Provider>
  );
};

export const useTeamContext = (): TeamContextType => {
  const context = useContext(TeamContext);

  if (!context) {
    throw new Error("useTeamContext must be used inside a TeamProvider");
  }
  return context;
};
